import { GaugeSeriesOption } from 'echarts';
import { useMemo } from 'react';
import { GaugeColorStop } from '@/panels-plugin/model/thresholds';
import { GAUGE_CHART_EPSILON, GaugeSeries } from './types';

// Make an axis line that only has the color that matches what the current value is
// this is used for most of the parts of the gauge to make sure they have the correct color
// even if that color does not fall onto the radial axis (ie, if the value is outside of max)
export const createValueColorAxisLine = (
  axisLine: GaugeSeriesOption['axisLine'],
  data: GaugeSeries,
  max?: number
): GaugeSeriesOption['axisLine'] => {
  // If colors aren't defined, then we have nothing to worry about, just return
  if (axisLine === undefined || axisLine.lineStyle === undefined || axisLine.lineStyle.color === undefined) {
    return axisLine;
  }

  const actualMax = max || 100;
  const value = data.value || 0;
  const valueToMaxRatio = value / actualMax;

  // find the color that this ratio falls into
  const colorIndex = axisLine.lineStyle.color.findIndex((step) => step[0] > valueToMaxRatio);

  let color: GaugeColorStop | undefined = undefined;
  if (colorIndex === -1) {
    color = axisLine.lineStyle.color[axisLine.lineStyle.color.length - 1];
  } else {
    color = axisLine.lineStyle.color[colorIndex];
  }

  if (color === undefined) {
    return axisLine;
  } else {
    return { ...axisLine, lineStyle: { ...axisLine.lineStyle, color: [[1, color[1]]] } };
  }
};

export const useValueColorAxisLine = (
  axisLine: GaugeSeriesOption['axisLine'],
  data: GaugeSeries,
  max?: number
): GaugeSeriesOption['axisLine'] => {
  return useMemo(() => createValueColorAxisLine(axisLine, data, max), [axisLine, data, max]);
};

const createCorrectedAxisLine = (axisLine: GaugeSeriesOption['axisLine']) => {
  // If colors aren't defined, then we have nothing to worry about, just return
  if (axisLine === undefined || axisLine.lineStyle === undefined || axisLine.lineStyle.color === undefined) {
    return axisLine;
  }

  const colors = axisLine.lineStyle.color;

  // This is an edge case for when a threshold is exactly at the endline of the gauge
  // for example, you might have everything LESS THAN 100 be red and 100 EXACTLY is green
  // to handle that, we check the last two steps of the colors.  They should be at [1-thresholdEpsilon]
  // and 1.  If that's the case, then we will roll back that threshold epsilon value just a bit so that the final
  // segment of the radial shows up.
  const lastStep = colors[colors.length - 1];
  const stepBeforeLast = colors[colors.length - 2];

  if (lastStep && lastStep[0] === 1 && stepBeforeLast && stepBeforeLast[0] === 1 - GAUGE_CHART_EPSILON) {
    // copy colors
    const correctedColors = colors.map((color) => [...color] as [number, string]);

    // modify down next to last step
    const correctedStepBeforeLast = correctedColors[correctedColors.length - 2];
    if (correctedStepBeforeLast) {
      // If there is a step before this one, we will only correct if that number is less than the 0.98 we're trying to set it to
      const stepBeforeThisOne = correctedColors[correctedColors.length - 3];
      if (stepBeforeThisOne) {
        if (stepBeforeThisOne[0] < 0.998) {
          correctedStepBeforeLast[0] = 0.998;
        }
      } else {
        correctedStepBeforeLast[0] = 0.998;
      }
    }

    return { ...axisLine, lineStyle: { ...axisLine.lineStyle, color: correctedColors } };
  }

  return axisLine;
};

export const useCorrectedAxisLine = (axisLine: GaugeSeriesOption['axisLine']): GaugeSeriesOption['axisLine'] => {
  return useMemo(() => createCorrectedAxisLine(axisLine), [axisLine]);
};
